import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Phone } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { CONTACT } from "@/data/content";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-sand min-h-screen">
        <Navbar />
        <div className="pt-44 pb-32 text-center px-5">
          <h1 className="font-serif text-4xl text-forest">Article not found</h1>
          <Button onClick={() => navigate("/blog")} className="mt-6 rounded-full bg-forest text-sand">Back to resources</Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-sand min-h-screen">
        <Navbar />
        <div className="pt-44 text-center text-ink2">Loading…</div>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="bg-sand min-h-screen">
      <Navbar />
      <main className="pt-32 md:pt-40 pb-24">
        <article className="mx-auto max-w-3xl px-5 md:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-ink2 hover:text-forest transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> All resources
          </Link>
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.15em] text-sage mb-5">
            <span>{post.category}</span>
            <span className="flex items-center gap-1 text-ink2"><Clock className="w-3 h-3" /> {post.read_minutes} min read</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-forest tracking-tight leading-[1.02]">{post.title}</h1>
          <p className="mt-5 text-lg text-ink2 leading-relaxed">{post.excerpt}</p>
        </article>

        <div className="mx-auto max-w-4xl px-5 md:px-8 my-12">
          <img src={post.cover_image} alt={post.title} className="w-full h-[380px] md:h-[480px] object-cover rounded-3xl" />
        </div>

        <article className="mx-auto max-w-3xl px-5 md:px-8">
          <div className="prose-content text-lg text-ink leading-relaxed space-y-6">
            {post.content.split("\n\n").map((para, i) => {
              if (para.trim().startsWith("- ")) {
                const items = para.split("\n").filter((l) => l.trim().startsWith("- "));
                return (
                  <ul key={i} className="list-disc pl-6 space-y-2 text-ink2">
                    {items.map((it, j) => <li key={j}>{it.replace("- ", "")}</li>)}
                  </ul>
                );
              }
              return <p key={i} className="text-ink2">{para}</p>;
            })}
          </div>

          <div className="mt-16 rounded-3xl bg-forest text-sand p-8 md:p-10 text-center">
            <h3 className="font-serif text-3xl md:text-4xl">Ready to take the first step?</h3>
            <p className="mt-3 text-sand/75 max-w-xl mx-auto">Our team is available right now to talk, confidentially and without judgement.</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate("/#admissions")} className="h-13 px-7 py-6 rounded-full bg-terracotta hover:bg-[#a8664b] text-white font-semibold">Get Help Today</Button>
              <a href={CONTACT.phoneHref} className="h-auto px-7 py-4 rounded-full border border-sand/40 text-sand hover:bg-sand hover:text-forest flex items-center justify-center gap-2 font-semibold transition-colors">
                <Phone className="w-5 h-5" /> {CONTACT.phone}
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
